import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useElements } from '@stripe/react-stripe-js';

export const TIP_PERCENTAGE_OPTIONS = [20, 25, 30] as const;

export const roundTipToNearestDollar = (amount: number): number => Math.round(Math.max(0, amount));

export const calcTipFromPercent = (invoiceAmount: number, percent: number): number =>
  roundTipToNearestDollar((invoiceAmount * percent) / 100);

export const formatPaymentMoney = (v: number): string => Math.max(0, v).toFixed(2);

type InvoicePaymentAmountPanelProps = {
  invoiceAmount: number;
  tipAmount: number;
  onTipChange: (tipDollars: number) => Promise<void>;
  tipUpdating?: boolean;
};

/** Refreshes Stripe Payment Element after the PaymentIntent amount changes (e.g. tip). */
export function PaymentIntentAmountSync(props: { syncKey: number }) {
  const { syncKey } = props;
  const elements = useElements();

  useEffect(() => {
    if (syncKey <= 0 || !elements) return;
    elements.fetchUpdates().catch((err) => {
      console.warn('Failed to refresh payment element after tip update', err);
    });
  }, [syncKey, elements]);

  return null;
}

function TipDialog(props: {
  isOpen: boolean;
  invoiceAmount: number;
  initialTip: number;
  onClose: () => void;
  onApply: (tipDollars: number) => Promise<void>;
  applying?: boolean;
}) {
  const { isOpen, invoiceAmount, initialTip, onClose, onApply, applying } = props;

  const [selectedPercent, setSelectedPercent] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const suggestedTips = useMemo(
    () =>
      TIP_PERCENTAGE_OPTIONS.map((pct) => ({
        percent: pct,
        amount: calcTipFromPercent(invoiceAmount, pct),
      })),
    [invoiceAmount]
  );

  useEffect(() => {
    if (!isOpen) return;
    setLocalError(null);
    setCustomAmount(initialTip > 0 ? formatPaymentMoney(initialTip) : '');

    const matchingPct = TIP_PERCENTAGE_OPTIONS.find((pct) => calcTipFromPercent(invoiceAmount, pct) === initialTip) ?? null;
    setSelectedPercent(matchingPct);
  }, [initialTip, invoiceAmount, isOpen]);

  const resolvedTip = useMemo(() => {
    if (selectedPercent != null) return calcTipFromPercent(invoiceAmount, selectedPercent);
    const parsed = parseFloat(customAmount.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return Math.round(parsed * 100) / 100;
  }, [customAmount, invoiceAmount, selectedPercent]);

  const handleSelectPercent = (pct: number) => {
    setSelectedPercent(pct);
    setCustomAmount('');
    setLocalError(null);
  };

  const handleCustomChange = (value: string) => {
    setSelectedPercent(null);
    setCustomAmount(value);
    setLocalError(null);
  };

  const handleApply = async () => {
    if (resolvedTip < 0 || !Number.isFinite(resolvedTip)) {
      setLocalError('Enter a valid tip amount.');
      return;
    }
    try {
      await onApply(resolvedTip);
      onClose();
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || err?.message || 'Failed to update tip.');
    }
  };

  const handleRemoveTip = async () => {
    try {
      await onApply(0);
      onClose();
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || err?.message || 'Failed to remove tip.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" aria-modal="true" role="dialog">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-0">Add a tip</h2>
            <p className="text-sm text-gray-500 mb-0 mt-0.5">Show your appreciation for great service.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
            disabled={applying}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Suggested</div>
            <div className="grid grid-cols-3 gap-2">
              {suggestedTips.map(({ percent, amount }) => {
                const selected = selectedPercent === percent;
                return (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => handleSelectPercent(percent)}
                    disabled={applying}
                    className={`rounded-xl border px-3 py-3 text-center transition-colors ${
                      selected
                        ? 'border-[#0B4778] bg-[#0B4778]/5 ring-1 ring-[#0B4778]'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-900">{percent}%</div>
                    <div className="text-xs text-gray-500 mt-0.5">${formatPaymentMoney(amount)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="custom-tip-amount" className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wide">
              Custom amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
              <input
                id="custom-tip-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={customAmount}
                onChange={(e) => handleCustomChange(e.target.value)}
                disabled={applying}
                className={`w-full rounded-xl border pl-7 pr-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0B4778]/30 ${
                  selectedPercent == null && customAmount ? 'border-[#0B4778] ring-1 ring-[#0B4778]' : 'border-gray-200'
                }`}
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-gray-600">Tip amount</span>
            <span className="font-semibold text-gray-900">${formatPaymentMoney(resolvedTip)}</span>
          </div>

          {localError ? <div className="text-sm text-red-600">{localError}</div> : null}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleApply}
            disabled={applying}
            className="aacore-btn-primary w-full justify-center"
          >
            {applying ? (
              'Updating…'
            ) : resolvedTip > 0 ? (
              <>
                Apply tip
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Continue without tip
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
          {initialTip > 0 ? (
            <button
              type="button"
              onClick={handleRemoveTip}
              disabled={applying}
              className="aacore-btn-outlined w-full justify-center text-sm"
            >
              <X className="w-4 h-4" />
              Remove tip
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function InvoicePaymentAmountPanel(props: InvoicePaymentAmountPanelProps) {
  const { invoiceAmount, tipAmount, onTipChange, tipUpdating } = props;
  const [dialogOpen, setDialogOpen] = useState(false);

  const totalDue = invoiceAmount + tipAmount;
  const hasTip = tipAmount > 0;

  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <>
      <div className="text-right">
        {hasTip ? (
          <div className="space-y-1.5">
            <div>
              <div className="text-xs text-gray-500 mb-0.5">INVOICE AMOUNT</div>
              <div className="text-lg font-semibold text-gray-900">${formatPaymentMoney(invoiceAmount)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-0.5">TIP</div>
              <div className="text-base font-semibold text-gray-900">${formatPaymentMoney(tipAmount)}</div>
              <button
                type="button"
                onClick={openDialog}
                disabled={tipUpdating}
                className="mt-1 inline-flex items-center text-sm font-semibold transition-colors hover:opacity-80 disabled:opacity-50"
                style={{ color: '#0B4778' }}
              >
                Change tip
              </button>
            </div>
            <div className="pt-1 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-0.5">AMOUNT DUE</div>
              <div className="text-2xl font-bold text-gray-900">${formatPaymentMoney(totalDue)}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-500 mb-1">AMOUNT DUE</div>
            <div className="text-2xl font-bold">${formatPaymentMoney(invoiceAmount)}</div>
            <button
              type="button"
              onClick={openDialog}
              disabled={tipUpdating}
              className="mt-2 inline-flex items-center text-sm font-semibold transition-colors hover:opacity-80 disabled:opacity-50"
              style={{ color: '#0B4778' }}
            >
              Add Tip
            </button>
          </>
        )}
      </div>

      <TipDialog
        isOpen={dialogOpen}
        invoiceAmount={invoiceAmount}
        initialTip={tipAmount}
        onClose={closeDialog}
        onApply={onTipChange}
        applying={tipUpdating}
      />
    </>
  );
}

export default InvoicePaymentAmountPanel;
